import React, { useState, useEffect, useMemo, useCallback, useRef } from "react";
import { useDispatch, useSelector } from "react-redux";
import { Search, ChevronDown, Filter, UserCircle2, Building, Briefcase, MapPin, Globe } from "lucide-react";
import LocationSearchInput from "./LocationSearchInput";
import SmartDatePicker from "./SmartDatePicker";
import SubcategoryFiltersPanel from "./SubcategoryFiltersPanel";
import SearchSpacesDialog from "./SearchSpacesDialog";
import { Popover as BasePopover, PopoverTrigger as BasePopoverTrigger, PopoverContent as BasePopoverContent } from "../shadcn/ui/popover";
import billingService from "../services/billingService";
import subcategoryFilterService from "../services/subcategoryFilterService";
import { setServiceCategories, setServiceSubcategories } from "../redux/actions/dashboard";
import CountrySelectorBadge from "./CountrySelectorBadge";
import logger from "../utils/logger";

const Popover = BasePopover as React.ComponentType<any>;
const PopoverTrigger = BasePopoverTrigger as React.ComponentType<any>;
const PopoverContent = BasePopoverContent as React.ComponentType<any>;


// eslint-disable-next-line @typescript-eslint/no-var-requires
const FramerMotion = require("framer-motion");
const motion = FramerMotion.motion;
const AnimatePresence = FramerMotion.AnimatePresence;

export interface SpaceSearchPayload {
  location: string;
  query: string;
  searchTarget?: "all" | "location" | "host" | "space";
  searchFilter?: string;
  type: string;
  categoryId: string;
  categoryName: string;
  subcategoryId: string;
  subcategoryName: string;
  dateRange: any;
  filterOptionIds?: number[];
  dropoffLocation?: string;
}

const SEARCH_PLACEHOLDERS = [
  "Search spaces…",
  "Offices in Kenya…",
  "Coworking spaces",
  "Search by host names",
  "Kenya",
  "Search by country…",
  "Ghana",
];

const CATEGORY_ORDER = ["work", "stay", "play", "move", "eat"];

function getCategoryOrderIndex(cat: any): number {
  const name = ((cat?.name || cat?.code || "") as string).toLowerCase().trim();
  for (let i = 0; i < CATEGORY_ORDER.length; i++) {
    if (name.includes(CATEGORY_ORDER[i])) {
      return i;
    }
  }
  return 99;
}

function sortCategories(cats: any[]) {
  return [...cats].sort((a, b) => getCategoryOrderIndex(a) - getCategoryOrderIndex(b));
}

interface SpaceSearchBarProps {
  isAuthenticated?: boolean;
  initialSearchQuery?: string;
  initialSearchFilter?: string;
  initialCategoryId?: string;
  initialSubcategoryId?: string;
  initialDateRange?: any;
  initialFilterOptionIds?: number[];
  initialDropoffLocation?: string;
  locationPlaceholder?: string;
  typePlaceholder?: string;
  datePlaceholder?: string;
  onSearch: (payload: SpaceSearchPayload) => void;
}

const SpaceSearchBar: React.FC<SpaceSearchBarProps> = ({
  isAuthenticated = false,
  initialSearchQuery = "",
  initialSearchFilter = "all",
  initialCategoryId = "",
  initialSubcategoryId = "",
  initialDateRange = null,
  initialFilterOptionIds = [],
  initialDropoffLocation = "",
  locationPlaceholder = "City or area",
  typePlaceholder = "Select workspace type",
  datePlaceholder = "Add date",
  onSearch,
}) => {
  const dispatch = useDispatch();
  const reduxCategories = useSelector((state: any) => state.dashboard?.serviceCategories || []);
  const reduxSubcategories = useSelector((state: any) => state.dashboard?.serviceSubcategories || []);

  const [locationQuery, setLocationQuery] = useState("");
  const [dropoffQuery, setDropoffQuery] = useState(initialDropoffLocation);
  const [searchQuery, setSearchQuery] = useState(initialSearchQuery);
  const [placeholderIndex, setPlaceholderIndex] = useState(0);
  const [activeCategoryId, setActiveCategoryId] = useState(initialCategoryId);
  const [selectedSubcategoryId, setSelectedSubcategoryId] = useState(initialSubcategoryId);
  const [dateRange, setDateRange] = useState<any>(initialDateRange);
  const [subDropdownOpen, setSubDropdownOpen] = useState(false);
  const [categories, setCategories] = useState<any[]>([]);
  const [subcategoriesByCategory, setSubcategoriesByCategory] = useState<Record<string, any[]>>({});
  const [categoriesLoading, setCategoriesLoading] = useState(false);

  // Subcategory Filters state
  const [subcategoryFilters, setSubcategoryFilters] = useState<any[]>([]);
  const [selectedFilterOptionIds, setSelectedFilterOptionIds] = useState<number[]>(initialFilterOptionIds);
  const [filtersLoading, setFiltersLoading] = useState(false);
  const [filterPopoverOpen, setFilterPopoverOpen] = useState(false);

  const [searchFilter, setSearchFilter] = useState<string>(initialSearchFilter || "all");
  const [searchTargetPopoverOpen, setSearchTargetPopoverOpen] = useState(false);

  const fulfillmentRef = useRef<HTMLDivElement>(null);
  const subDropdownRef = useRef<HTMLDivElement>(null);
  const searchFilterRef = useRef<HTMLDivElement>(null);

  const getFilterIcon = (filterKey: string) => {
    switch (filterKey) {
      case "organizations":
        return <UserCircle2 className="w-3.5 h-3.5 text-blue-600 flex-shrink-0" />;
      case "buildings":
        return <Building className="w-3.5 h-3.5 text-blue-600 flex-shrink-0" />;
      case "spaces":
        return <Briefcase className="w-3.5 h-3.5 text-blue-600 flex-shrink-0" />;
      case "cities":
        return <MapPin className="w-3.5 h-3.5 text-blue-600 flex-shrink-0" />;
      case "countries":
        return <Globe className="w-3.5 h-3.5 text-blue-600 flex-shrink-0" />;
      case "all":
      default:
        return <Search className="w-3.5 h-3.5 text-gray-500 flex-shrink-0" />;
    }
  };

  // Close dropdowns on outside click
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (fulfillmentRef.current && !fulfillmentRef.current.contains(event.target as Node)) {
        setFilterPopoverOpen(false);
      }
      if (subDropdownRef.current && !subDropdownRef.current.contains(event.target as Node)) {
        setSubDropdownOpen(false);
      }
      if (searchFilterRef.current && !searchFilterRef.current.contains(event.target as Node)) {
        setSearchTargetPopoverOpen(false);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    document.addEventListener("click", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
      document.removeEventListener("click", handleClickOutside);
    };
  }, []);

  // Sync state if initial props change dynamically from parent
  useEffect(() => {
    if (initialSearchQuery) setSearchQuery(initialSearchQuery);
  }, [initialSearchQuery]);

  useEffect(() => {
    if (initialSearchFilter) setSearchFilter(initialSearchFilter);
  }, [initialSearchFilter]);

  useEffect(() => {
    if (initialSubcategoryId) setSelectedSubcategoryId(initialSubcategoryId);
  }, [initialSubcategoryId]);

  useEffect(() => {
    if (initialDateRange) setDateRange(initialDateRange);
  }, [initialDateRange]);

  useEffect(() => {
    if (initialFilterOptionIds && initialFilterOptionIds.length > 0) {
      setSelectedFilterOptionIds(initialFilterOptionIds);
    }
  }, [initialFilterOptionIds]);

  useEffect(() => {
    if (initialDropoffLocation) setDropoffQuery(initialDropoffLocation);
  }, [initialDropoffLocation]);

  // Default activeCategoryId if not provided in props or when categories first load
  useEffect(() => {
    if (categories.length > 0) {
      if (!activeCategoryId) {
        const targetId = initialCategoryId && categories.some(c => String(c.id) === String(initialCategoryId))
          ? String(initialCategoryId)
          : String(categories[0].id);
        setActiveCategoryId(targetId);
      }
    }
  }, [categories]);

  // Rotating placeholder effect for non-Move search input
  useEffect(() => {
    if (searchQuery) return;
    let intervalId: any;
    const timeoutId = setTimeout(() => {
      setPlaceholderIndex(prev => (prev + 1) % SEARCH_PLACEHOLDERS.length);
      intervalId = setInterval(() => {
        setPlaceholderIndex(prev => (prev + 1) % SEARCH_PLACEHOLDERS.length);
      }, 3500);
    }, 3500);
    return () => {
      clearTimeout(timeoutId);
      if (intervalId) clearInterval(intervalId);
    };
  }, [searchQuery]);

  const categoriesLoadedRef = useRef(false);

  // Read categories from the Redux store (populated after login). Fall back to a local
  // fetch for public flows (e.g. /home) where the store may be empty.
  useEffect(() => {
    if (categoriesLoadedRef.current) {
      if (reduxCategories.length > 0) {
        const sortedRedux = sortCategories(reduxCategories);
        setCategories(sortedRedux);
      }
      return;
    }

    if (reduxCategories.length > 0) {
      const sortedRedux = sortCategories(reduxCategories);
      setCategories(sortedRedux);
      setSubcategoriesByCategory(prev => {
        const next = { ...prev };
        reduxCategories.forEach((cat: any) => {
          const catSubs = cat.subcategories || [];
          const reduxSubs = reduxSubcategories.filter((sub: any) => String(sub.categoryId) === String(cat.id));
          const combined = catSubs.length > 0 ? catSubs : reduxSubs;
          if (combined.length > 0) {
            next[cat.id] = combined;
          }
        });
        return next;
      });
      categoriesLoadedRef.current = true;
      return;
    }

    let cancelled = false;
    setCategoriesLoading(true);

    const loadLocal = async () => {
      try {
        const fetchCategories = isAuthenticated
          ? billingService
              .getAllServiceCategories({ type: "END_USER" })
              .catch(() => billingService.getServiceCategories({ orderBy: "name:desc" }))
          : billingService
              .getPublicServiceCategories({ orderBy: "name:desc" })
              .catch(() => billingService.getAllServiceCategories({ type: "END_USER" }));

        const res = await fetchCategories;
        if (cancelled || !res) return;

        categoriesLoadedRef.current = true;

        const raw = Array.isArray(res.data)
          ? res.data
          : res.data?.data || res.data?.content || res.data?.items || [];

        const withNested: any[] = raw.map((cat: any) => ({
          id: cat.id,
          name: cat.name,
          subcategories: cat.subcategories || [],
        }));

        const sorted = sortCategories(withNested);
        setCategories(sorted);
        setSubcategoriesByCategory(prev => {
          const next = { ...prev };
          sorted.forEach(cat => {
            if (!next[cat.id] || next[cat.id].length === 0) {
              next[cat.id] = cat.subcategories || [];
            }
          });
          return next;
        });

        // Populate the store so the authenticated store stays in sync when possible
        if (isAuthenticated && withNested.length > 0) {
          const flatCategories = withNested.map(({ subcategories, ...cat }: any) => cat);
          const flatSubcategories = withNested.flatMap((cat: any) =>
            (cat.subcategories || []).map((sub: any) => ({ ...sub, categoryId: cat.id }))
          );
          dispatch(setServiceCategories(flatCategories));
          dispatch(setServiceSubcategories(flatSubcategories));
        }
      } catch (err) {
        logger.warn("SpaceSearchBar: failed to load categories", err);
      } finally {
        if (!cancelled) setCategoriesLoading(false);
      }
    };

    loadLocal();
    return () => {
      cancelled = true;
    };
  }, [reduxCategories, reduxSubcategories, isAuthenticated, dispatch]);

  // Lazy-fetch subcategories for the active category if not present in state
  useEffect(() => {
    if (!activeCategoryId) return;
    if (subcategoriesByCategory[String(activeCategoryId)] && subcategoriesByCategory[String(activeCategoryId)].length > 0) {
      return;
    }

    let cancelled = false;
    const fetchSubcategoriesForCategory = async () => {
      try {
        let subs: any[] = [];
        // service-categories/all requires a session; visitors go straight to the public registry
        const resAll = isAuthenticated
          ? await billingService.getAllServiceCategories({ type: "END_USER" }).catch(() => null)
          : null;

        if (resAll) {
          const raw = Array.isArray(resAll.data) ? resAll.data : resAll.data?.data || resAll.data?.content || [];
          const matchedCat = raw.find((cat: any) => String(cat.id) === String(activeCategoryId));
          if (matchedCat && Array.isArray(matchedCat.subcategories) && matchedCat.subcategories.length > 0) {
            subs = matchedCat.subcategories;
          }
        }

        if (subs.length === 0) {
          const numCatId = Number(activeCategoryId);
          if (!isNaN(numCatId)) {
            const fetchSub = isAuthenticated
              ? billingService.getServiceSubcategories(numCatId).catch(() => null)
              : billingService.getPublicServiceSubcategories(numCatId).catch(() => null);
            const res = await fetchSub;
            if (res) {
              subs = Array.isArray(res.data)
                ? res.data
                : res.data?.data || res.data?.content || res.data?.items || [];
            }
          }
        }

        if (cancelled || subs.length === 0) return;

        setSubcategoriesByCategory(prev => ({
          ...prev,
          [String(activeCategoryId)]: subs,
        }));
      } catch (err) {
        logger.warn("SpaceSearchBar: error fetching subcategories for active category", activeCategoryId, err);
      }
    };

    fetchSubcategoriesForCategory();
    return () => {
      cancelled = true;
    };
  }, [activeCategoryId, subcategoriesByCategory, isAuthenticated]);

  const activeCategory = categories.find(c => String(c.id) === String(activeCategoryId)) || null;

  // Derive Move category and ride subcategory flags (code-agnostic)
  const isMoveCategory = useMemo(() => {
    if (!activeCategory) return false;
    const code = (activeCategory.code || '').toUpperCase();
    const name = (activeCategory.name || '').toUpperCase();
    return code === 'MOVE' || name.includes('MOVE');
  }, [activeCategory]);

  const activeSubcategories = useMemo(() => {
    const fetched = subcategoriesByCategory[String(activeCategoryId)] || [];
    if (fetched.length > 0) return fetched;
    if (isMoveCategory) {
      return [
        { id: "201", name: "Find a Car", code: "STANDARD_RIDE" },
        { id: "202", name: "Rent a Vehicle", code: "CAR_RENTAL" },
        { id: "203", name: "Chauffeur Service", code: "CHAUFFEUR" },
        { id: "204", name: "Fleet Subscription", code: "FLEET" },
      ];
    }
    return [];
  }, [subcategoriesByCategory, activeCategoryId, isMoveCategory]);

  // Ensure default Move subcategory is selected if Move category is active but subcategory is empty
  useEffect(() => {
    if (isMoveCategory && !selectedSubcategoryId && activeSubcategories.length > 0) {
      const defaultSub = activeSubcategories.find((s: any) => String(s.code) === "CAR_RENTAL" || s.name?.toLowerCase().includes("rent")) || activeSubcategories[1] || activeSubcategories[0];
      if (defaultSub) {
        setSelectedSubcategoryId(String(defaultSub.id));
      }
    }
  }, [isMoveCategory, selectedSubcategoryId, activeSubcategories]);

  const selectedSub = useMemo(
    () => activeSubcategories.find((s: any) => String(s.id) === String(selectedSubcategoryId)) || null,
    [activeSubcategories, selectedSubcategoryId]
  );

  const isRideSubcategory = useMemo(() => {
    if (!selectedSub) return false;
    const code = (selectedSub.code || '').toUpperCase();
    const name = (selectedSub.name || '').toLowerCase();
    return code === 'STANDARD_RIDE' || name.includes('ride');
  }, [selectedSub]);

  // Fetch subcategory-filters when a Move subcategory is selected
  useEffect(() => {
    if (!selectedSubcategoryId) {
      setSubcategoryFilters([]);
      setSelectedFilterOptionIds([]);
      return;
    }
    const numId = Number(selectedSubcategoryId);
    if (isNaN(numId)) return;

    setFiltersLoading(true);
    subcategoryFilterService.getSubcategoryFilters(numId)
      .then(res => {
        const raw = Array.isArray(res.data) ? res.data : res.data?.data || res.data?.content || [];
        setSubcategoryFilters(raw);
      })
      .catch(err => {
        logger.warn("SpaceSearchBar: error fetching subcategory filters", err);
        setSubcategoryFilters([]);
      })
      .finally(() => setFiltersLoading(false));
  }, [selectedSubcategoryId]);

  const toggleFilterOption = useCallback((optionId: number) => {
    setSelectedFilterOptionIds(prev =>
      prev.includes(optionId) ? prev.filter(id => id !== optionId) : [...prev, optionId]
    );
  }, []);

  const handleSubmit = useCallback(
    (e: React.FormEvent) => {
      e.preventDefault();
      setFilterPopoverOpen(false);
      setSearchTargetPopoverOpen(false);
      const selectedSub = activeSubcategories.find(s => String(s.id) === String(selectedSubcategoryId)) || null;
      onSearch({
        location: locationQuery.trim(),
        query: searchQuery.trim(),
        searchFilter,
        searchTarget: searchFilter as any,
        type: (activeCategory?.name || "").toLowerCase(),
        categoryId: activeCategory ? String(activeCategory.id) : "",
        categoryName: activeCategory?.name || "",
        subcategoryId: selectedSub ? String(selectedSub.id) : "",
        subcategoryName: selectedSub?.name || "",
        dateRange,
        filterOptionIds: selectedFilterOptionIds,
        ...(isRideSubcategory && dropoffQuery.trim() ? { dropoffLocation: dropoffQuery.trim() } : {}),
      } as any);
    },
    [locationQuery, dropoffQuery, searchQuery, searchFilter, activeCategory, activeSubcategories, selectedSubcategoryId, dateRange, selectedFilterOptionIds, isRideSubcategory, onSearch]
  );

  if (categoriesLoading && categories.length === 0) {
    return null;
  }

  const showDropoff = isRideSubcategory;

  return (
    <motion.div
      animate={{ maxWidth: isRideSubcategory ? '84rem' : isMoveCategory ? '76rem' : '64rem' }}
      transition={{ type: 'spring', stiffness: 280, damping: 30 }}
      className="bg-white p-4 sm:p-6 rounded-3xl text-gray-800 shadow-2xl w-full mx-auto border border-gray-100 box-border text-left"
    >
      <div className="flex gap-4 border-b border-gray-100 pb-3 mb-4 text-xs font-bold text-gray-500 overflow-x-auto text-left">
        {categories.map(cat => (
          <button
            key={cat.id}
            type="button"
            onClick={() => {
              const newCatId = String(cat.id);
              setActiveCategoryId(newCatId);
              setFilterPopoverOpen(false);
              setSubcategoryFilters([]);
              setSelectedFilterOptionIds([]);

              // If switching to MOVE category, set default subcategory to 'Rent a Vehicle'
              const isCatMove = (cat.name || "").toLowerCase().includes("move") || (cat.code || "").toLowerCase().includes("move");
              let newSubId = "";
              if (isCatMove) {
                const moveSubs = subcategoriesByCategory[newCatId] || [
                  { id: "201", name: "Find a Car", code: "STANDARD_RIDE" },
                  { id: "202", name: "Rent a Vehicle", code: "CAR_RENTAL" },
                ];
                const defaultSub = moveSubs.find(s => String(s.code) === "CAR_RENTAL" || s.name.toLowerCase().includes("rent")) || moveSubs[1] || moveSubs[0];
                newSubId = defaultSub ? String(defaultSub.id) : "";
                setSelectedSubcategoryId(newSubId);
              } else {
                setSelectedSubcategoryId("");
              }
            }}
            className={`capitalize pb-1 transition-all whitespace-nowrap text-left ${
              String(cat.id) === String(activeCategoryId)
                ? "text-blue-600 border-b-2 border-blue-600 font-extrabold"
                : "hover:text-gray-900"
            }`}
          >
            {cat.name}
          </button>
        ))}
      </div>

      <form onSubmit={handleSubmit} className="grid grid-cols-1 md:grid-cols-12 gap-2.5 items-end text-left w-full max-w-full">
        <motion.div
          {...{ layout: true } as any}
          transition={{ type: 'spring', stiffness: 280, damping: 30 }}
          className={`${
            isRideSubcategory
              ? 'md:col-span-2'
              : selectedSubcategoryId && isMoveCategory
                ? 'md:col-span-2'
                : 'md:col-span-4'
          } space-y-1 min-w-0 w-full overflow-hidden text-left`}
        >
          <span className="text-[10px] font-bold text-gray-400 uppercase tracking-wider block px-1 text-left h-4 leading-4 truncate">
            {isMoveCategory
              ? isRideSubcategory
                ? 'PICKUP LOCATION'
                : 'LOCATION / CITY'
              : 'SEARCH'}
          </span>
          {isMoveCategory ? (
            <LocationSearchInput
              value={locationQuery}
              onChange={(val: string) => setLocationQuery(val)}
              placeholder={
                isRideSubcategory
                  ? 'Where to pick you up?'
                  : 'Where are you picking up?'
              }
            />
          ) : (
            <div className="relative w-full h-11 bg-gray-50 rounded-xl px-2.5 border border-gray-200 flex items-center gap-2 focus-within:border-blue-300 transition-colors">
              <div ref={searchFilterRef} className="relative flex-shrink-0 flex items-center">
                <Popover open={searchTargetPopoverOpen} onOpenChange={setSearchTargetPopoverOpen}>
                  <PopoverTrigger asChild>
                    <button
                      type="button"
                      aria-label="Filter search target"
                      className="flex items-center gap-1 py-1 px-1.5 rounded-lg hover:bg-gray-200/60 active:bg-gray-200 transition-colors text-gray-600 focus:outline-none"
                    >
                      <motion.div
                        key={searchFilter}
                        initial={{ scale: 0.8, opacity: 0 }}
                        animate={{ scale: 1, opacity: 1 }}
                        transition={{ duration: 0.2 }}
                        className="flex items-center justify-center"
                      >
                        {getFilterIcon(searchFilter)}
                      </motion.div>
                      <ChevronDown className={`w-3 h-3 text-gray-400 transition-transform ${searchTargetPopoverOpen ? 'rotate-180' : ''}`} />
                    </button>
                  </PopoverTrigger>
                  <PopoverContent
                    align="start"
                    sideOffset={8}
                    className="p-0 border border-gray-200 bg-white shadow-xl rounded-xl w-56 z-50"
                  >
                    <SearchSpacesDialog
                      searchFilter={searchFilter}
                      onSearchFilterChange={(filterKey: string) => {
                        setSearchFilter(filterKey);
                        setSearchTargetPopoverOpen(false);
                      }}
                    />
                  </PopoverContent>
                </Popover>
              </div>
              <div className="w-[1px] h-5 bg-gray-200 flex-shrink-0" />
              <div className="relative flex-1 min-w-0 h-full flex items-center overflow-hidden text-left">
                <input
                  type="text"
                  value={searchQuery}
                  onChange={e => setSearchQuery(e.target.value)}
                  placeholder=""
                  autoComplete="off"
                  className="w-full h-full bg-transparent border-0 outline-none text-xs font-medium text-gray-900 focus:outline-none focus:ring-0 p-0 relative z-10 text-left min-w-0"
                />
                {!searchQuery && (
                  <div className="pointer-events-none absolute left-0 top-0 bottom-0 flex items-center text-xs text-gray-400 w-full overflow-hidden text-left min-w-0">
                    <AnimatePresence mode="wait">
                      <motion.span
                        key={placeholderIndex}
                        initial={{ y: -16, opacity: 0 }}
                        animate={{ y: 0, opacity: 1 }}
                        exit={{ y: 16, opacity: 0 }}
                        transition={{ duration: 0.4, ease: "easeInOut" }}
                        className="truncate whitespace-nowrap block w-full text-left min-w-0"
                      >
                        {SEARCH_PLACEHOLDERS[placeholderIndex]}
                      </motion.span>
                    </AnimatePresence>
                  </div>
                )}
              </div>
            </div>
          )}
        </motion.div>

        {/* DROP-OFF LOCATION — ride sub selected under Move */}
        <AnimatePresence>
          {showDropoff && (
            <motion.div
              initial={{ opacity: 0, scale: 0.96 }}
              animate={{ opacity: 1, scale: 1 }}
              exit={{ opacity: 0, scale: 0.96 }}
              transition={{ duration: 0.3, ease: [0.16, 1, 0.3, 1] }}
              className="md:col-span-2 space-y-1 min-w-0 w-full overflow-hidden text-left"
            >
              <span className="text-[10px] font-bold text-gray-400 uppercase tracking-wider block px-1 text-left whitespace-nowrap h-4 leading-4 truncate">
                DROP-OFF LOCATION
              </span>
              <LocationSearchInput
                value={dropoffQuery}
                onChange={(val: string) => setDropoffQuery(val)}
                placeholder="Where are you dropping off?"
              />
            </motion.div>
          )}
        </AnimatePresence>

        {/* HOST / KEYWORD — optional host name search under Move */}
        {isMoveCategory && (
          <motion.div
            {...{ layout: true } as any}
            transition={{ type: 'spring', stiffness: 280, damping: 30 }}
            className="md:col-span-2 space-y-1 min-w-0 w-full overflow-hidden text-left"
          >
            <span className="text-[10px] font-bold text-gray-400 uppercase tracking-wider block px-1 text-left h-4 leading-4 truncate">
              HOST / KEYWORD
            </span>
            <div className="relative w-full h-11 bg-gray-50 rounded-xl px-3 border border-gray-200 flex items-center gap-2 focus-within:border-blue-300 transition-colors overflow-hidden">
              <Search className="w-4 h-4 text-gray-400 flex-shrink-0" />
              <input
                type="text"
                value={searchQuery}
                onChange={e => setSearchQuery(e.target.value)}
                placeholder="Host name or vehicle..."
                autoComplete="off"
                className="w-full h-full bg-transparent border-0 outline-none text-xs font-medium text-gray-900 focus:outline-none focus:ring-0 p-0 relative z-10 text-left min-w-0"
              />
            </div>
          </motion.div>
        )}

        {/* VEHICLE TYPE / WHAT DO YOU NEED? */}
        <motion.div
          {...{ layout: true } as any}
          transition={{ type: 'spring', stiffness: 280, damping: 30 }}
          className={`${
            isMoveCategory
              ? 'md:col-span-2'
              : 'md:col-span-3'
          } space-y-1 min-w-0 w-full text-left`}
        >
          <span className="text-[10px] font-bold text-gray-400 uppercase tracking-wider block px-1 text-left h-4 leading-4 truncate whitespace-nowrap">
            WHAT DO YOU NEED?
          </span>
          <div
            ref={subDropdownRef}
            className="relative w-full text-left min-w-0"
          >
            <button
              type="button"
              onClick={() => setSubDropdownOpen(o => !o)}
              className="w-full h-11 bg-gray-50 rounded-xl px-3 border border-gray-200 text-xs font-medium text-gray-800 flex items-center justify-between text-left focus:outline-none hover:bg-gray-100/80 transition-colors relative"
            >
              <div className="flex-1 min-w-0 pr-6 relative h-full flex items-center overflow-hidden">
                <span className="truncate block w-full text-left pointer-events-none">
                  {selectedSubcategoryId
                    ? activeSubcategories.find(s => String(s.id) === String(selectedSubcategoryId))?.name ||
                      (isMoveCategory ? "All Vehicles" : "WHAT DO YOU NEED?")
                    : isMoveCategory ? "All Vehicles" : "WHAT DO YOU NEED?"}
                </span>
              </div>
              <ChevronDown className={`w-3.5 h-3.5 text-gray-500 absolute right-3 top-1/2 -translate-y-1/2 flex-shrink-0 transition-transform ${subDropdownOpen ? "rotate-180" : ""}`} />
            </button>
            <AnimatePresence>
              {subDropdownOpen && (
                <motion.div
                  initial={{ opacity: 0, y: 8, scale: 0.98 }}
                  animate={{ opacity: 1, y: 0, scale: 1 }}
                  exit={{ opacity: 0, y: 6, scale: 0.98 }}
                  transition={{ duration: 0.2, ease: "easeOut" }}
                  className="absolute top-full left-0 mt-1.5 w-full bg-white rounded-xl shadow-2xl border border-gray-100 py-1.5 z-50 max-h-60 overflow-y-auto"
                >
                  <button
                    type="button"
                    onClick={() => {
                      setSelectedSubcategoryId("");
                      setSubDropdownOpen(false);
                      onSearch({
                        location: locationQuery.trim(),
                        query: searchQuery.trim(),
                        type: (activeCategory?.name || "").toLowerCase(),
                        categoryId: activeCategory ? String(activeCategory.id) : "",
                        categoryName: activeCategory?.name || "",
                        subcategoryId: "",
                        subcategoryName: "",
                        dateRange,
                        filterOptionIds: selectedFilterOptionIds,
                      } as any);
                    }}
                    className={`w-full text-left px-3.5 py-2 text-xs font-medium hover:bg-blue-50 border-b border-gray-100 truncate block ${
                      !selectedSubcategoryId ? "text-blue-600 font-bold bg-blue-50" : "text-gray-400"
                    }`}
                  >
                    {isMoveCategory ? "All Vehicles" : "All types"}
                  </button>
                  {activeSubcategories.map(sub => (
                    <button
                      key={sub.id}
                      type="button"
                      onClick={() => {
                        const newSubId = String(sub.id);
                        setSelectedSubcategoryId(newSubId);
                        setSubDropdownOpen(false);
                        onSearch({
                          location: locationQuery.trim(),
                          query: searchQuery.trim(),
                          type: (activeCategory?.name || "").toLowerCase(),
                          categoryId: activeCategory ? String(activeCategory.id) : "",
                          categoryName: activeCategory?.name || "",
                          subcategoryId: newSubId,
                          subcategoryName: sub.name || "",
                          dateRange,
                          filterOptionIds: selectedFilterOptionIds,
                        } as any);
                      }}
                      className={`w-full text-left px-3.5 py-2 text-xs font-medium transition-colors hover:bg-blue-50 hover:text-blue-700 truncate block ${
                        String(selectedSubcategoryId) === String(sub.id) ? "text-blue-600 font-bold bg-blue-50" : "text-gray-700"
                      }`}
                    >
                      {sub.name}
                    </button>
                  ))}
                </motion.div>
              )}
            </AnimatePresence>
          </div>
        </motion.div>

        {/* FULFILLMENT & OPTIONS COLUMN — when MOVE subcategory is selected */}
        <AnimatePresence>
          {isMoveCategory && selectedSubcategoryId && (
            <motion.div
              initial={{ opacity: 0, scale: 0.96 }}
              animate={{ opacity: 1, scale: 1 }}
              exit={{ opacity: 0, scale: 0.96 }}
              transition={{ duration: 0.3, ease: [0.16, 1, 0.3, 1] }}
              className="md:col-span-2 space-y-1 min-w-0 w-full text-left"
            >
              <span className="text-[10px] font-bold text-gray-400 uppercase tracking-wider block px-1 text-left h-4 leading-4 truncate whitespace-nowrap">
                FULFILLMENT
              </span>
              <div ref={fulfillmentRef} className="relative w-full text-left min-w-0">
                <button
                  type="button"
                  onClick={() => setFilterPopoverOpen(o => !o)}
                  className={`w-full h-11 rounded-xl px-3 border text-xs font-medium flex items-center justify-between text-left focus:outline-none transition-colors relative ${
                    selectedFilterOptionIds.length > 0
                      ? "bg-blue-50 border-blue-200 text-blue-700 font-semibold"
                      : "bg-gray-50 border-gray-200 text-gray-800 hover:bg-gray-100/80"
                  }`}
                >
                  <div className="flex items-center gap-1.5 min-w-0 truncate">
                    <Filter className="w-3.5 h-3.5 text-blue-600 flex-shrink-0" />
                    <span className="truncate">
                      {selectedFilterOptionIds.length > 0
                        ? `${selectedFilterOptionIds.length} option${selectedFilterOptionIds.length > 1 ? 's' : ''}`
                        : "Fulfillment & Options"}
                    </span>
                  </div>
                  <ChevronDown className={`w-3.5 h-3.5 text-gray-500 transition-transform flex-shrink-0 ml-1 ${filterPopoverOpen ? 'rotate-180' : ''}`} />
                </button>
                <AnimatePresence>
                  {filterPopoverOpen && (
                    <motion.div
                      initial={{ opacity: 0, y: 8, scale: 0.98 }}
                      animate={{ opacity: 1, y: 0, scale: 1 }}
                      exit={{ opacity: 0, y: 6, scale: 0.98 }}
                      transition={{ duration: 0.2, ease: "easeOut" }}
                      className="absolute top-full left-0 mt-1.5 w-64 bg-white rounded-xl shadow-2xl border border-gray-100 p-2 z-50 max-h-64 overflow-y-auto"
                    >
                      <SubcategoryFiltersPanel
                        filters={subcategoryFilters}
                        selectedOptionIds={selectedFilterOptionIds}
                        onToggleOption={toggleFilterOption}
                        isLoading={filtersLoading}
                        isError={false}
                      />
                    </motion.div>
                  )}
                </AnimatePresence>
              </div>
            </motion.div>
          )}
        </AnimatePresence>

        {/* DATE COLUMN */}
        <motion.div
          {...{ layout: true } as any}
          transition={{ type: 'spring', stiffness: 280, damping: 30 }}
          className={`${
            isRideSubcategory
              ? 'md:col-span-2'
              : selectedSubcategoryId && isMoveCategory
                ? 'md:col-span-2'
                : 'md:col-span-3'
          } space-y-1 min-w-0 w-full overflow-hidden text-left`}
        >
          <span className="text-[10px] font-bold text-gray-400 uppercase tracking-wider block px-1 text-left h-4 leading-4 truncate">
            {isMoveCategory
              ? isRideSubcategory
                ? 'RIDE DATE'
                : 'PICKUP DATE'
              : 'WHEN?'}
          </span>
          <div className="bg-gray-50 rounded-xl border border-gray-200 h-11 flex items-center text-left overflow-hidden min-w-0 w-full">
            <SmartDatePicker
              value={dateRange}
              onChange={(val: any) => setDateRange(val)}
              placeholder={datePlaceholder}
              className="w-full text-xs font-medium border-0 bg-transparent shadow-none h-full text-left pl-3 justify-start"
            />
          </div>
        </motion.div>

        <motion.div
          {...{ layout: true } as any}
          transition={{ type: 'spring', stiffness: 280, damping: 30 }}
          className="md:col-span-2 space-y-1"
        >
          <span className="text-[10px] font-bold text-transparent uppercase tracking-wider block px-1 select-none hidden md:block h-4 leading-4">SEARCH</span>
          <button
            type="submit"
            className="w-full h-11 bg-blue-600 hover:bg-blue-700 text-white rounded-xl text-xs font-bold flex items-center justify-center gap-2 shadow-md transition-all whitespace-nowrap px-2"
          >
            <Search className="w-4 h-4 flex-shrink-0" />
            {isMoveCategory
              ? 'Find a Car'
              : 'Search'}
          </button>
        </motion.div>
      </form>
    </motion.div>
  );
};

export default SpaceSearchBar;
